import React from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import Select from 'react-select';
import { compose, withProps, branch } from 'recompose';

import * as styles from './styles.css';
import * as sharedStyles from '../styles.css';
import formControl from '../formControl';
import withUsers from '../../../containers/withUsers';
import withPagination from '../../PaginatedItems/withPagination';
import { Endpoints } from '../../../lib/LeanLinkService';
import requireProps from '../../../lib/requireProps';
import EntityIcon from '../../EntityIcon/EntityIcon';
import { withTranslation } from 'react-i18next';
import { props } from 'lodash/fp';

function UserOption({ label, display }) {
  return (
    <span className={`${sharedStyles.option} ${display ? styles.display : ''}`}>
      <EntityIcon type={'user'} />
      {label}
    </span>
  );
}

UserOption.propTypes = {
  label: PropTypes.string.isRequired,
  display: PropTypes.bool.isRequired,
};

class UserSelection extends React.Component {
  static propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })).isRequired,
    input: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string, // Just a side effect of redux-form initializing to string
        PropTypes.object,
        PropTypes.number,
      ]),
      onChange: PropTypes.func.isRequired,
      onBlur: PropTypes.func,
    }).isRequired,
    disabled: PropTypes.bool,
    isEditable: PropTypes.bool,
    multi: PropTypes.bool,
    clearable: PropTypes.bool,
  };

  static defaultProps = {
    multi: false,
    disabled: false,
    isEditable: true,
    clearable: true,
  };

  handleValues = (value) => {
    if (value === null) {
      return value;
    }

    return (this.props.multi ? new List((value || []).map(v => v.value)) : (value || {}).value);
  };

  val = () => {
    if (!this.props.input.value) {
      return this.props.input.value;
    }

    return (this.props.multi ? this.props.input.value.toJS() : this.props.input.value);
  };

  render() {
    if (!this.props.isEditable) {
      if ((!this.props.input.value || this.props.input.value.size === 0)) {
        return (<i className={sharedStyles.value}>{this.props.t("Ingen användare vald.")}</i>);
      }

      const values = this.props.multi ? this.props.input.value : [this.props.input.value];
      return (
        <div className={sharedStyles.optionsContainer}>
          {this.props.options
            .filter(user => values.includes(user.value))
            .map(user => (
              <UserOption
                display
                key={user.value}
                label={user.label}
              />
            ))}
        </div>
      );
    }
    return (
      <Select
        disabled={this.props.disabled}
        placeholder={this.props.t("Användare...")}
        name="search"
        autoload={false}
        valueRenderer={UserOption}
        optionRenderer={UserOption}
        options={this.props.options}
        onChange={values => this.props.input.onChange(this.handleValues(values))}
        onBlur={() => this.props.input.onBlur(this.props.input.value)}
        value={this.val()}
        className={`${sharedStyles.inputContainer}`}
        ignoreAccents={false}
        multi={this.props.multi}
        clearable={this.props.clearable}
      />
    );
  }
}

export default compose(
  withTranslation(),
  branch(
    ({ isEditable }) => isEditable !== false,
    compose(
      withProps(
        (viewAll) => ({
          url: Endpoints.users.list({ enabled: viewAll ? undefined : true, limit: 1000 }),
        }),
      ),
      withPagination(),
      withProps(({ ids }) => ({
        userIds: ids,
      })),
    ),
    withProps(({ input: { value }, multi }) => {
      if (!value) {
        return {};
      }
      if (!multi) {
        return {
          userIds: [value],
        };
      }
      return {
        userIds: value,
      };
    }),
  ),
  requireProps(['userIds']),
  withUsers,
  requireProps(['users']),
  withProps(({ users, showEmail, emailType }) => {
    const options = new List(users.map(user => {
      let emailToShow = user.email;
      switch (emailType) {
        case 'customerOrderConfirmation':
          if (user.customerOrderConfirmationEmail !== null) {
            emailToShow = user.customerOrderConfirmationEmail;
          }
          break;
        case 'resourceOrderConfirmation':
          if (user.resourceOrderConfirmationEmail !== null) {
            emailToShow = user.resourceOrderConfirmationEmail;
          }
          break;
        default:
          emailToShow = user.email;
      }
      return ({
        value: user.id,
        label: showEmail ? `${user.name} <${emailToShow}>` : user.name,
      });
    }));
    return { options };
  }),
  branch(
    ({ searchWithoutUser }) => !!searchWithoutUser,
    withProps(({ options, }) => ({
      options: options.push({
        value: 'null',
        label: 'Ingen ansvarig användare',
      }),
    })),
  ),
  withProps(({ options }) => ({
    options: options.toJS(),
  })),
  withProps(({ input, forceCheck, isLoading, meta, t }) => {
    if (!forceCheck) {
      return {};
    }

    if ((!input.value || input.value.size === 0) && !isLoading) {
      return {
        indicator: true,
        meta: {
          ...meta,
          touched: true,
          error: t('En användare måste vara vald'),
        },
      };
    }

    return {};
  }),
  formControl,
)(UserSelection);
